import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { LiveCall } from "@/lib/liveCall";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Camera,
  Loader2,
  PhoneOff,
  Users,
  Wifi,
  Video,
  Sparkles,
  Circle,
  Square,
  ChevronRight,
} from "lucide-react";

const MAX_WARMUP_MS = 60000;

export default function LiveCandidate() {
  const { roomId } = useParams();
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const warmupVideoRef = useRef(null);
  const warmupStreamRef = useRef(null);
  const warmupRecorderRef = useRef(null);
  const warmupChunksRef = useRef([]);
  const warmupTimerRef = useRef(null);
  const warmupElapsedRef = useRef(0);
  const callRef = useRef(null);
  const localStreamRef = useRef(null);

  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  // step: 'form' | 'warmup' | 'live'
  const [step, setStep] = useState("form");
  const [form, setForm] = useState({ candidate_name: "", candidate_email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [permissionError, setPermissionError] = useState(null);
  const [hasRemote, setHasRemote] = useState(false);
  const [connState, setConnState] = useState("new");
  const [connTimedOut, setConnTimedOut] = useState(false);
  const connTimerRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [ended, setEnded] = useState(false);

  // Warmup state
  const [warmupQuestion, setWarmupQuestion] = useState(null);
  const [warmupPhase, setWarmupPhase] = useState("ready"); // ready | recording | uploading | done
  const [warmupElapsed, setWarmupElapsed] = useState(0);
  const [confidenceMsg, setConfidenceMsg] = useState(null);

  useEffect(() => {
    api
      .get(`/live-rooms/${roomId}`)
      .then(({ data }) => {
        setRoom(data);
        // Beacon: tell the host the candidate landed on the form
        api
          .post(`/live-rooms/${roomId}/candidate-step`, { step: "form" })
          .catch(() => {});
      })
      .catch(() => toast.error("Invalid live interview link"))
      .finally(() => setLoading(false));
  }, [roomId]);

  const join = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/live-rooms/${roomId}/join`, form);
      setRoom(data);
      // Skip-warmup support: jump straight to live if recruiter opted out
      if (data.skip_warmup) {
        api
          .post(`/live-rooms/${roomId}/candidate-step`, { step: "live" })
          .catch(() => {});
        setStep("live");
        return;
      }
      // Otherwise: fetch warmup question, then move to warmup step
      try {
        const { data: w } = await api.get(
          `/live-rooms/${roomId}/warmup-question`
        );
        setWarmupQuestion(w.question);
      } catch {
        setWarmupQuestion("To start — tell us a little about yourself.");
      }
      api
        .post(`/live-rooms/${roomId}/candidate-step`, { step: "warmup" })
        .catch(() => {});
      setStep("warmup");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not join");
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Warmup recording ----
  const initWarmupCamera = async () => {
    if (warmupStreamRef.current) {
      if (warmupVideoRef.current) {
        warmupVideoRef.current.srcObject = warmupStreamRef.current;
        warmupVideoRef.current.muted = true;
      }
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      warmupStreamRef.current = stream;
      if (warmupVideoRef.current) {
        warmupVideoRef.current.srcObject = stream;
        warmupVideoRef.current.muted = true;
      }
    } catch {
      setPermissionError(
        "Camera and microphone permission is required."
      );
    }
  };

  useEffect(() => {
    if (step === "warmup" && warmupPhase === "ready") {
      initWarmupCamera();
    }
  }, [step, warmupPhase]); // eslint-disable-line

  const startWarmupRecording = () => {
    const stream = warmupStreamRef.current;
    if (!stream) return;
    warmupChunksRef.current = [];
    let mime = "video/webm;codecs=vp8,opus";
    if (!window.MediaRecorder || !MediaRecorder.isTypeSupported(mime)) {
      mime = "video/webm";
    }
    const rec = new MediaRecorder(stream, { mimeType: mime });
    rec.ondataavailable = (e) => {
      if (e.data?.size) warmupChunksRef.current.push(e.data);
    };
    rec.onstop = async () => {
      const blob = new Blob(warmupChunksRef.current, { type: "video/webm" });
      const duration = warmupElapsedRef.current;
      setWarmupPhase("uploading");
      const fd = new FormData();
      fd.append("duration_ms", String(duration));
      fd.append("file", blob, "warmup.webm");
      try {
        const { data } = await api.post(
          `/live-rooms/${roomId}/warmup`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        setConfidenceMsg(data.confidence_message);
      } catch {
        setConfidenceMsg("You've got this — take a breath and go for it.");
      }
      setWarmupPhase("done");
    };
    warmupRecorderRef.current = rec;
    rec.start();
    setWarmupElapsed(0);
    warmupElapsedRef.current = 0;
    setWarmupPhase("recording");
    const startedAt = Date.now();
    warmupTimerRef.current = setInterval(() => {
      const e = Date.now() - startedAt;
      setWarmupElapsed(e);
      warmupElapsedRef.current = e;
      if (e >= MAX_WARMUP_MS) stopWarmupRecording();
    }, 200);
  };

  const stopWarmupRecording = () => {
    if (warmupTimerRef.current) {
      clearInterval(warmupTimerRef.current);
      warmupTimerRef.current = null;
    }
    const rec = warmupRecorderRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
  };

  const [warmupRegenerating, setWarmupRegenerating] = useState(false);

  const regenerateWarmup = async () => {
    setWarmupRegenerating(true);
    try {
      const { data } = await api.post(
        `/live-rooms/${roomId}/warmup-question/regenerate`
      );
      setWarmupQuestion(data.question);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not regenerate question"
      );
    } finally {
      setWarmupRegenerating(false);
    }
  };

  const skipWarmup = () => {
    if (warmupStreamRef.current) {
      warmupStreamRef.current.getTracks().forEach((t) => t.stop());
      warmupStreamRef.current = null;
    }
    api
      .post(`/live-rooms/${roomId}/candidate-step`, { step: "live" })
      .catch(() => {});
    setStep("live");
  };

  const enterLive = () => {
    if (warmupStreamRef.current) {
      warmupStreamRef.current.getTracks().forEach((t) => t.stop());
      warmupStreamRef.current = null;
    }
    api
      .post(`/live-rooms/${roomId}/candidate-step`, { step: "live" })
      .catch(() => {});
    setStep("live");
  };



  // After warmup, start media + call
  useEffect(() => {
    if (step !== "live") return;
    let cancelled = false;
    (async () => {
      try {
        let stream = localStreamRef.current;
        if (!stream || stream.getTracks().every((t) => t.readyState === "ended")) {
          stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: true,
          });
          if (cancelled) {
            stream.getTracks().forEach((t) => t.stop());
            return;
          }
          localStreamRef.current = stream;
        }
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
          localVideoRef.current.muted = true;
          localVideoRef.current.playsInline = true;
          try {
            await localVideoRef.current.play();
          } catch {}
        }
        if (callRef.current) return;
        const call = new LiveCall({
          roomId,
          role: "candidate",
          onRemoteStream: (remote) => {
            if (remoteVideoRef.current) {
              remoteVideoRef.current.srcObject = remote;
              remoteVideoRef.current.playsInline = true;
              remoteVideoRef.current.play().catch(() => {});
            }
            setHasRemote(true);
            if (connTimerRef.current) {
              clearTimeout(connTimerRef.current);
              connTimerRef.current = null;
            }
            setConnTimedOut(false);
          },
          onPeerLeft: () => {
            setHasRemote(false);
            setEnded(true);
            if (connTimerRef.current) {
              clearTimeout(connTimerRef.current);
              connTimerRef.current = null;
            }
          },
          onConnectionState: (s) => setConnState(s),
          onPeerJoined: () => {
            if (connTimerRef.current) clearTimeout(connTimerRef.current);
            connTimerRef.current = setTimeout(() => {
              if (!callRef.current?.remoteStream?.getTracks().length) {
                setConnTimedOut(true);
              }
            }, 15000);
          },
        });
        callRef.current = call;
        await call.start(stream);
      } catch {
        setPermissionError(
          "Camera and microphone permission required to join the call."
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, roomId]);

  // True unmount cleanup
  useEffect(() => {
    return () => {
      try {
        if (callRef.current) callRef.current.hangup();
      } catch {}
      try {
        if (localStreamRef.current) {
          localStreamRef.current.getTracks().forEach((t) => t.stop());
          localStreamRef.current = null;
        }
      } catch {}
    };
  }, []);

  const toggleMute = () => {
    const stream = localVideoRef.current?.srcObject;
    if (!stream) return;
    stream.getAudioTracks().forEach((t) => (t.enabled = muted));
    setMuted(!muted);
  };

  const toggleCamera = () => {
    const stream = localVideoRef.current?.srcObject;
    if (!stream) return;
    stream.getVideoTracks().forEach((t) => (t.enabled = cameraOff));
    setCameraOff(!cameraOff);
  };

  const leave = () => {
    if (callRef.current) callRef.current.hangup();
    setEnded(true);
  };

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-900 text-white">
        Loading…
      </main>
    );
  }
  if (!room) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-900 text-white">
        Invalid live link.
      </main>
    );
  }

  if (ended || room.status === "ended") {
    return (
      <main
        data-testid="live-candidate-ended"
        className="flex min-h-screen flex-col items-center justify-center bg-slate-900 px-6 text-center text-white"
      >
        <PhoneOff className="h-10 w-10 text-blue-300" />
        <h1 className="mt-6 font-heading text-3xl font-semibold">
          Call ended
        </h1>
        <p className="mt-3 max-w-md text-sm text-slate-400">
          Thanks for the conversation. The interviewer will follow up via email.
        </p>
      </main>
    );
  }

  const job = room.job || {};

  if (step === "form") {
    return (
      <main
        data-testid="live-candidate-start"
        className="grain-overlay min-h-screen bg-slate-900 text-white"
      >
        <div className="mx-auto grid min-h-screen max-w-6xl gap-12 px-6 py-16 lg:grid-cols-2 lg:items-center">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-300">
              <Video className="h-3 w-3" /> Live interview
            </div>
            <h1 className="mt-4 font-heading text-4xl font-semibold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              {job?.title || "Live interview"}
            </h1>
            <p className="mt-6 max-w-lg text-base text-slate-300">
              You've been invited to a live video interview. Enter your details
              to join the room — your camera and microphone will be used.
            </p>
          </div>
          <form
            onSubmit={join}
            className="border border-white/10 bg-white/5 p-8 backdrop-blur-xl"
          >
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-300">
              <Camera className="h-3 w-3" /> Join the call
            </div>
            <div className="mt-6 space-y-5">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
                  Full name
                </Label>
                <Input
                  required
                  data-testid="live-candidate-name-input"
                  value={form.candidate_name}
                  onChange={(e) =>
                    setForm({ ...form, candidate_name: e.target.value })
                  }
                  className="mt-2 rounded-sm border-white/20 bg-white/5 text-white"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
                  Email
                </Label>
                <Input
                  type="email"
                  required
                  data-testid="live-candidate-email-input"
                  value={form.candidate_email}
                  onChange={(e) =>
                    setForm({ ...form, candidate_email: e.target.value })
                  }
                  className="mt-2 rounded-sm border-white/20 bg-white/5 text-white"
                />
              </div>
            </div>
            <Button
              type="submit"
              data-testid="live-candidate-join-btn"
              disabled={submitting}
              className="mt-8 w-full rounded-sm bg-blue-600 py-6 hover:bg-blue-500"
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Join live interview
            </Button>
          </form>
        </div>
      </main>
    );
  }

  if (step === "warmup") {
    const secs = Math.floor(warmupElapsed / 1000);
    const mm = String(Math.floor(secs / 60)).padStart(2, "0");
    const ss = String(secs % 60).padStart(2, "0");
    return (
      <main
        data-testid="live-candidate-warmup"
        className="grain-overlay min-h-screen bg-slate-900 text-white"
      >
        <div className="mx-auto flex min-h-screen max-w-3xl flex-col px-6 py-10">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-300">
            <Sparkles className="h-3 w-3" /> Warm-up · just for you
          </div>
          <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
            Take a deep breath. Loosen up.
          </h1>
          <p className="mt-3 max-w-xl text-sm text-slate-400">
            Record a quick answer below — only you and the AI coach will see it.
            <strong className="text-amber-200">
              {" "}You must finish this step to enter the live call with the
              interviewer.
            </strong>{" "}
            They are already waiting for you on the other side.
          </p>

          <div className="mt-8 border border-white/10 bg-white/5 p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300">
                Question
              </div>
              {warmupPhase === "ready" && warmupQuestion && (
                <button
                  data-testid="warmup-regen-btn"
                  onClick={regenerateWarmup}
                  disabled={warmupRegenerating}
                  className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 hover:text-blue-300 disabled:opacity-50"
                >
                  {warmupRegenerating ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3" />
                  )}
                  Try a different one
                </button>
              )}
            </div>
            <p
              data-testid="warmup-question-text"
              className="mt-2 font-heading text-xl font-medium leading-snug sm:text-2xl"
            >
              {warmupQuestion || "Loading…"}
            </p>
          </div>

          <div className="relative mt-6 overflow-hidden border border-white/10 bg-black">
            <video
              ref={warmupVideoRef}
              autoPlay
              playsInline
              muted
              data-testid="warmup-video"
              className="aspect-video w-full bg-black object-cover"
            />
            {warmupPhase === "recording" && (
              <div className="absolute left-4 top-4 flex items-center gap-2 border border-red-500 bg-black/60 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-red-400">
                <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
                Recording · {mm}:{ss}
              </div>
            )}
            {warmupPhase === "uploading" && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-center">
                <div>
                  <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin text-blue-300" />
                  <div className="font-heading text-base">
                    Sending it to your AI coach…
                  </div>
                </div>
              </div>
            )}
            {warmupPhase === "done" && confidenceMsg && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/80 p-6 text-center">
                <div>
                  <Sparkles className="mx-auto mb-3 h-8 w-8 text-blue-300" />
                  <p
                    data-testid="warmup-confidence-msg"
                    className="mx-auto max-w-md font-heading text-xl font-medium text-white"
                  >
                    {confidenceMsg}
                  </p>
                </div>
              </div>
            )}
          </div>

          {permissionError && (
            <div className="mt-4 border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-300">
              {permissionError}
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <button
              data-testid="warmup-skip-btn"
              onClick={skipWarmup}
              className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 hover:text-slate-300"
            >
              Skip warm-up
            </button>
            {warmupPhase === "ready" && (
              <Button
                data-testid="warmup-start-btn"
                onClick={startWarmupRecording}
                className="rounded-sm bg-red-600 px-6 py-6 hover:bg-red-500"
              >
                <Circle className="mr-2 h-4 w-4 fill-current" />
                Start warm-up
              </Button>
            )}
            {warmupPhase === "recording" && (
              <Button
                data-testid="warmup-stop-btn"
                onClick={stopWarmupRecording}
                className="rounded-sm bg-white px-6 py-6 text-slate-900 hover:bg-slate-100"
              >
                <Square className="mr-2 h-4 w-4 fill-current" />
                Stop
              </Button>
            )}
            {warmupPhase === "done" && (
              <Button
                data-testid="enter-live-btn"
                onClick={enterLive}
                className="rounded-sm bg-blue-600 px-6 py-6 hover:bg-blue-500"
              >
                Join live call
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main
      data-testid="live-candidate-page"
      className="grain-overlay min-h-screen bg-slate-900 text-white"
    >
      <div className="mx-auto flex min-h-screen max-w-5xl flex-col gap-4 px-6 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-300">
            <Video className="h-3 w-3" /> Live · {job?.title}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <span className="inline-flex items-center gap-1">
              <Wifi className="h-3 w-3" /> {connState}
            </span>
            <span className="inline-flex items-center gap-1">
              <Users className="h-3 w-3" />{" "}
              {hasRemote ? "Interviewer connected" : "Waiting for interviewer…"}
            </span>
          </div>
        </div>

        <div className="relative overflow-hidden border border-white/10 bg-black">
          {/* Always mount remote video so ref is attached when stream arrives. */}
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            data-testid="live-candidate-remote-video"
            className={`aspect-video w-full bg-black object-cover ${hasRemote ? "" : "hidden"}`}
          />
          {!hasRemote && (connTimedOut || connState === "failed") && (
            <div
              data-testid="live-candidate-conn-failed"
              className="flex aspect-video w-full flex-col items-center justify-center bg-black p-6 text-center"
            >
              <div className="inline-flex items-center gap-2 rounded-sm border border-rose-300 bg-rose-50 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-rose-700">
                <Wifi className="h-3 w-3" /> Connection failed
              </div>
              <div className="mt-4 max-w-md font-heading text-xl text-white">
                We couldn't connect you to the interviewer.
              </div>
              <div className="mt-2 max-w-md text-sm text-slate-400">
                This is usually a restrictive network (corporate VPN, strict firewall,
                or some hotel WiFi). Try switching to a different network (mobile hotspot
                or home WiFi) and re-open the link.
              </div>
              <button
                type="button"
                data-testid="live-candidate-conn-retry"
                onClick={() => window.location.reload()}
                className="mt-5 rounded-sm border border-white/30 bg-transparent px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-white hover:bg-white/10"
              >
                Retry connection
              </button>
            </div>
          )}
          {!hasRemote && !connTimedOut && connState !== "failed" && (
            <div
              data-testid="live-candidate-waiting"
              className="flex aspect-video w-full flex-col items-center justify-center bg-black p-6 text-center text-slate-400"
            >
              <Loader2 className="h-8 w-8 animate-spin text-blue-300" />
              <div className="mt-4 font-heading text-lg">
                Waiting for the interviewer to join…
              </div>
            </div>
          )}
          <div className="absolute bottom-4 right-4 h-32 w-44 overflow-hidden border border-white/20 bg-black shadow-lg sm:h-40 sm:w-56">
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              data-testid="live-candidate-local-video"
              className="h-full w-full object-cover"
            />
          </div>
        </div>

        {permissionError && (
          <div className="border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-300">
            {permissionError}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-white/5 p-3">
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="live-candidate-mute-btn"
              variant="outline"
              onClick={toggleMute}
              className="rounded-sm border-white/30 bg-transparent text-white hover:bg-white/10"
            >
              {muted ? "Unmute" : "Mute"}
            </Button>
            <Button
              data-testid="live-candidate-camera-btn"
              variant="outline"
              onClick={toggleCamera}
              className="rounded-sm border-white/30 bg-transparent text-white hover:bg-white/10"
            >
              {cameraOff ? "Camera on" : "Camera off"}
            </Button>
          </div>
          <Button
            data-testid="live-candidate-leave-btn"
            onClick={leave}
            className="rounded-sm bg-red-600 hover:bg-red-500"
          >
            <PhoneOff className="mr-2 h-4 w-4" />
            Leave call
          </Button>
        </div>
      </div>
    </main>
  );
}
